import type { AccountDetails } from '../../../../api/types/account';
import type { StudentDetailsForm, StudentEntryForm } from './SignUpStudentDetails';

export const NZ_POSTCODE = '0600';
export const NZ_STATE = 'NZ';

export type SignUpUserDetails = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
};

const getParentDetails = (accountDetails: AccountDetails): SignUpUserDetails => {
  return {
    firstName: accountDetails.firstName,
    lastName: accountDetails.lastName,
    email: accountDetails.email,
    phone: accountDetails.phone,
    country: accountDetails.country,
  };
};

const getStudentDetails = (accountDetails: AccountDetails): SignUpUserDetails => {
  return {
    firstName: accountDetails.firstName,
    lastName: accountDetails.lastName,
    email: accountDetails.email,
    phone: accountDetails.phone,
    country: accountDetails.country,
  };
};

const addNZPostCodeAndState = (students: StudentEntryForm[]) => {
  return students.map((student) => {
    const isNZ = student.countryCode === 'NZ';

    return {
      ...student,
      // If the student is in NZ, postcode and state are not captured
      // but the backend does not accept null values so we populate them
      postCode: !student.postCode && isNZ ? NZ_POSTCODE : student.postCode,
      state: !student.state && isNZ ? NZ_STATE : student.state,
    };
  });
};

/* 
  User selected "Signing up for Myself"
  The user himself is the student
*/
const mapUserToStudents = (accountDetails: AccountDetails, formData: StudentDetailsForm) => {
  const isNZ = formData.countryCode === 'NZ';
  return [
    {
      schoolYear: formData.schoolYear,
      countryCode: formData.countryCode,
      // If the student is in NZ, postcode and state are not captured
      // but the backend does not accept null values so we populate them
      postCode: !formData.postCode && isNZ ? NZ_POSTCODE : formData.postCode,
      state: !formData.state && isNZ ? NZ_STATE : formData.state,
      // The user doesn't input their first name in the Student Details page when signing up for themselves
      firstName: accountDetails.firstName,
    },
  ];
};

type SignUpPayload = {
  signUpUser: SignUpUserDetails;
  students: StudentEntryForm[];
};

const prepareSignUpPayload = (
  isParent: boolean,
  accountDetails: AccountDetails,
  formData: StudentDetailsForm
): SignUpPayload => {
  const signUpUser = isParent ? getParentDetails(accountDetails) : getStudentDetails(accountDetails);

  const students = isParent ? addNZPostCodeAndState(formData.students) : mapUserToStudents(accountDetails, formData);

  return {
    signUpUser,
    students,
  };
};

export default prepareSignUpPayload;
